/**
 * แนวลูกค้า (owner 17 ต.ค. 69 · api/app/ai/script/style.py · docs/script.md "แนวลูกค้า"): reading the API's `style`
 * safely, the panel's one line "แนวลูกค้า: สุภาพ กระชับ · สนใจ ลดน้ำหนัก/ฟิต", and the "ตอบภายใน 5 นาที" badge of a curt
 * customer (the analysis of the shop's LINE chats: a curt customer answered within 5 minutes closed 32 %, after 2–12
 * hours 13 %). No Vue here.
 */
import { toStyleKey, type CustomerMotive, type CustomerStyle } from '../api/script/types'
import type { ConversationSummary } from '../api/types'
import { formatDuration, waitSeconds } from './inbox'

/** The badge turns to a warning after this long without our reply. */
export const QUICK_REPLY_SEC = 300
export const QUICK_REPLY_LABEL = 'ตอบภายใน 5 นาที'

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])

/** The API's `style`, or null when it is missing / not one of the five styles. */
export function normalizeStyle(raw: unknown): CustomerStyle | null {
  if (typeof raw !== 'object' || raw === null) return null
  const s = raw as Partial<CustomerStyle>
  const key = toStyleKey(s.key)
  if (!key) return null
  return {
    key,
    label: typeof s.label === 'string' && s.label.trim() ? s.label : key,
    returning: !!s.returning,
    motives: arr<CustomerMotive>(s.motives).filter((m) => !!m && typeof m.label === 'string' && !!m.label),
    basis: typeof s.basis === 'number' && s.basis >= 0 ? s.basis : 0,
  }
}

/** The panel line: "สุภาพ กระชับ" + "สนใจ ลดน้ำหนัก/ฟิต, เก็บรักษา/อุ่น" (null = nothing said) + a hover text. */
export function styleLine(s: CustomerStyle): { label: string; motives: string | null; title: string } {
  const read = s.basis >= 2 ? `อ่านจากข้อความลูกค้า ${s.basis} ข้อความล่าสุด` : 'ลูกค้ายังพิมพ์น้อย ใช้แบบสุภาพ กระชับไว้ก่อน'
  const said = s.motives.length ? ` · เรื่องที่ลูกค้าพูดถึง: ${s.motives.map((m) => m.label).join(', ')}` : ''
  return {
    label: s.label,
    motives: s.motives.length ? `สนใจ ${s.motives.map((m) => m.label).join(', ')}` : null,
    title: `${read}${said} · ระบบอ่านจากคำที่ลูกค้าพิมพ์ ไม่ใช้ AI`,
  }
}

export interface QuickReply {
  /** Seconds the customer has been waiting for us. */
  waited: number
  /** Past QUICK_REPLY_SEC: warning colour. */
  late: boolean
}

/**
 * "ตอบภายใน 5 นาที": a curt customer (style_key `curt`) waiting for OUR reply in an open chat nobody handed to the AI.
 * Gone once the shop replies (needs_reply false). null = no badge.
 */
export function quickReply(c: ConversationSummary | null | undefined, nowMs: number): QuickReply | null {
  if (!c || c.style_key !== 'curt' || c.status !== 'open' || c.ai_mode) return null
  const waited = waitSeconds(c, nowMs)
  if (waited === null) return null
  return { waited, late: waited >= QUICK_REPLY_SEC }
}

/** "ลูกค้ารอ 7 นาที" / "ลูกค้าเพิ่งทัก" — the wait shown with the badge. */
export const waitedText = (q: QuickReply): string => (q.waited < 60 ? 'ลูกค้าเพิ่งทัก' : `ลูกค้ารอ ${formatDuration(q.waited)}`)

/** The badge's hover text. */
export const quickReplyTitle = (q: QuickReply): string =>
  `ลูกค้าพิมพ์สั้น ไม่มีหางเสียง ตอบเร็วช่วยปิดการขาย · ${waitedText(q)}${q.late ? ' · เกิน 5 นาทีแล้ว' : ''}`
